import { Pipe, PipeTransform } from '@angular/core';
import { VimeoService } from './vimeo.service';

@Pipe({
  name: 'vimeoThumbnailsById',
})
export class VimeoThumbnailsByIdPipe implements PipeTransform {
  constructor(private vimeoService: VimeoService) {}

  transform(uri: string): Promise<{ link: string; width?: number; height?: number; aspectRatio?: string } | null> {
    return Promise.all([
      this.vimeoService.getVideoThumbnails(uri).catch(() => null),
      this.vimeoService.getVideoById(uri).catch(() => null),
    ]).then(([response, video]) => {
      const data = response?.data;
      const activeThumbnail = data?.find(thumbnail => thumbnail?.active);
      const sizes = [...(activeThumbnail?.sizes ?? []), ...(video?.pictures?.sizes ?? [])];

      if (!sizes?.length) {
        return null;
      }

      const videoWidth = Number(video?.width);
      const videoHeight = Number(video?.height);
      const hasVideoDimensions =
        Number.isFinite(videoWidth) && Number.isFinite(videoHeight) && videoWidth > 0 && videoHeight > 0;
      const videoRatio = hasVideoDimensions ? videoWidth / videoHeight : null;
      const thumbnail = this.getBestThumbnailSize(sizes, videoRatio);
      const width = Number(thumbnail?.width);
      const height = Number(thumbnail?.height);
      const hasThumbnailDimensions = Number.isFinite(width) && Number.isFinite(height) && width > 0 && height > 0;
      const thumbnailRatio = hasThumbnailDimensions ? width / height : null;
      const isThumbnailSameOrientation =
        !videoRatio ||
        !thumbnailRatio ||
        (videoRatio >= 1 && thumbnailRatio >= 1) ||
        (videoRatio < 1 && thumbnailRatio < 1);
      const link = isThumbnailSameOrientation
        ? thumbnail?.link ?? video?.pictures?.base_link ?? thumbnail?.link_with_play_button
        : video?.pictures?.base_link ?? thumbnail?.link ?? thumbnail?.link_with_play_button;

      if (!link) {
        return null;
      }

      return {
        link,
        width: hasVideoDimensions ? videoWidth : hasThumbnailDimensions ? width : undefined,
        height: hasVideoDimensions ? videoHeight : hasThumbnailDimensions ? height : undefined,
        aspectRatio: hasVideoDimensions
          ? `${videoWidth} / ${videoHeight}`
          : hasThumbnailDimensions
            ? `${width} / ${height}`
            : undefined,
      };
    });
  }

  private getBestThumbnailSize(sizes: any[], targetRatio: number | null): any {
    if (!targetRatio) {
      return sizes?.find(size => size?.width >= 640) ?? sizes?.[0];
    }

    return (
      [...sizes]
        .filter(size => Number(size?.width) > 0 && Number(size?.height) > 0)
        .sort((a, b) => {
          const aRatioDifference = Math.abs(Number(a.width) / Number(a.height) - targetRatio);
          const bRatioDifference = Math.abs(Number(b.width) / Number(b.height) - targetRatio);

          if (aRatioDifference !== bRatioDifference) {
            return aRatioDifference - bRatioDifference;
          }

          return Number(b.width) - Number(a.width);
        })[0] ?? sizes?.[0]
    );
  }
}
